import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle, Tag } from "lucide-react";
import { getLatestArticles } from "@/data/articles";

import academyTeamImage from "@/assets/academy-team.jpg";
const categoryLabels: Record<string, string> = {
  bambini: "Bambini", ragazzi: "Ragazzi", adulti: "Adulti",
  generale: "Generale", zona: "Zona", agonisti: "Agonisti"
};

const Academy = () => {
  const blogArticles = getLatestArticles(3);
  return (
    <Layout>
      <Helmet>
        <title>L'Academy - Taekwondo Mattei Roma</title>
        <meta name="description" content="Scopri l'Academy Taekwondo Mattei: la nostra storia, il metodo educativo e l'approccio alla formazione sportiva a Roma." />
        <link rel="canonical" href="https://taekwondomattei.com/academy" />
        <meta property="og:title" content="L'Academy - Taekwondo Mattei Roma" />
        <meta property="og:description" content="Scopri l'Academy Taekwondo Mattei: la nostra storia, il metodo educativo e l'approccio alla formazione sportiva a Roma." />
        <meta property="og:image" content="https://taekwondomattei.com/og-academy.jpg" />
        <meta property="og:url" content="https://taekwondomattei.com/academy" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="L'Academy - Taekwondo Mattei Roma" />
        <meta name="twitter:description" content="Scopri l'Academy Taekwondo Mattei: la nostra storia, il metodo educativo e l'approccio alla formazione sportiva a Roma." />
        <meta name="twitter:image" content="https://taekwondomattei.com/og-academy.jpg" />
      </Helmet>
      <SeoBreadcrumb items={[{ label: "L'Academy" }]} />
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={academyTeamImage}
            alt="La nostra palestra"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative z-10 container-wide mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gold mb-6">
              L'Academy Taekwondo Mattei Roma
            </h1>
            <p className="font-body text-xl text-gold/80">
              Una scuola di Taekwondo a Roma con radici solide e uno sguardo al futuro.
            </p>
          </div>
        </div>
      </section>

      {/* Chi siamo */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader
                title="Chi siamo"
                subtitle=""
                centered={false}
              />
              <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
                <p>
                  Taekwondo Mattei è una scuola di arti marziali a Roma che mette al centro 
                  la formazione della persona prima ancora dell'atleta. Non formiamo campioni 
                  a tutti i costi: formiamo prima persone.
                </p>
                <p>
                  Il nostro approccio integra lo sviluppo di competenze trasversali fondamentali: 
                  socializzazione, problem solving, comunicazione verbale e non verbale, 
                  autostima, autoefficacia e gestione delle emozioni.
                </p>
                <p>
                  Ogni allievo è accolto nel rispetto dei propri tempi e delle proprie capacità, 
                  in un percorso educativo che va ben oltre il tatami.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={academyTeamImage}
                alt="Allenamento in palestra"
                className="rounded-2xl shadow-large"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* La storia */}
      <section className="section-padding bg-secondary">
        <div className="container-narrow mx-auto">
          <SectionHeader
            title="La nostra storia"
            subtitle="Continuità e innovazione"
          />
          
          <div className="space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-6">
              <div className="font-display text-4xl font-bold text-gold">1992</div>
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  La fondazione
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Tutto ha inizio nel 1992, quando il Maestro Francesco Tortorella e la Maestra 
                  Carmen Goffredo decidono di dare vita a una scuola di Taekwondo. È l'inizio 
                  di un progetto che unisce disciplina marziale e formazione della persona, 
                  con l'ambizione di costruire qualcosa di duraturo.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-6">
              <div className="font-display text-4xl font-bold text-gold">2000</div>
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  La crescita
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  A partire dal 2000, la scuola si apre ai più giovani con turni dedicati 
                  ai bambini. Una scelta che nel corso degli anni avrebbe portato risultati 
                  importanti, sia sul piano sportivo che su quello educativo, consolidando 
                  l'identità della scuola.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-6">
              <div className="font-display text-4xl font-bold text-gold">2015</div>
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  Una nuova generazione
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Dal 2015 prende forma una transizione naturale: gli atleti cresciuti 
                  nella scuola diventano a loro volta allenatori e punti di riferimento. 
                  Una nuova generazione che porta avanti i valori originari con la stessa 
                  passione di chi li ha formati.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-6">
              <div className="font-display text-4xl font-bold text-gold">2025</div>
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  Continuità e nuova fase
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Con la scomparsa del Maestro Francesco Tortorella, i maestri
                  cresciuti all'interno della scuola assumono la guida operativa
                  della squadra e dell'Academy, dando continuità al progetto
                  avviato dai fondatori. La Maestra Carmen Goffredo resta punto
                  di riferimento dell'Academy, custode dei valori che hanno
                  guidato oltre trent'anni di lavoro. Una nuova fase che porta
                  avanti la stessa dedizione e la tradizione dell'arte marziale,
                  con lo sguardo rivolto alla crescita futura e la gratitudine
                  per il percorso costruito insieme a Francesco Tortorella.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Approccio */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Il nostro approccio"
            subtitle="Educazione sportiva a 360 gradi"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Sicurezza prima di tutto",
                items: [
                  "Riscaldamento specifico per ogni fascia d'età",
                  "Protezioni obbligatorie durante il combattimento",
                  "Allenamenti graduali e progressivi",
                  "Attenzione alle esigenze individuali"
                ]
              },
              {
                title: "Metodo educativo",
                items: [
                  "Rispetto delle regole e dei compagni",
                  "Gestione delle emozioni e dello stress",
                  "Obiettivi raggiungibili e misurabili",
                  "Feedback costruttivi e incoraggiamento"
                ]
              },
              {
                title: "Percorsi per età",
                items: [
                  "Programmi specifici per bambini 6-10 anni",
                  "Preparazione atletica per adolescenti",
                  "Corsi per adulti principianti",
                  "Percorsi agonistici per chi lo desidera"
                ]
              },
              {
                title: "Comunità",
                items: [
                  "Esami di cintura in un clima sereno e di supporto",
                  "Stage tecnici con maestri di alto livello",
                  "Eventi e momenti di condivisione fuori dal tatami",
                  "Crescita collettiva nel tempo"
                ]
              }
            ].map((block) => (
              <div key={block.title} className="card-elevated p-8 border border-border">
                <h3 className="font-display text-xl font-semibold text-foreground mb-6">
                  {block.title}
                </h3>
                <ul className="space-y-3">
                  {block.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="font-body text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articoli correlati */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Dal nostro blog"
            subtitle="Approfondimenti su metodo, crescita e Taekwondo"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogArticles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group card-elevated border border-border p-6"
              >
                <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                  {categoryLabels[article.category] || article.category}
                </span>
                <h3 className="font-display text-lg font-semibold text-foreground mt-4 group-hover:text-gold transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
                  {article.excerpt}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog" className="font-body text-gold hover:text-gold-light transition-colors inline-flex items-center gap-2">
              Tutti gli articoli <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-link */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <h2 className="sr-only">Scopri anche</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/percorsi" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                I Percorsi →
              </h3>
              <p className="font-body text-muted-foreground">
                Scopri i programmi per bambini, ragazzi, adulti e agonisti.
              </p>
            </Link>
            <Link to="/maestri" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                I Maestri →
              </h3>
              <p className="font-body text-muted-foreground">
                Conosci i nostri istruttori federali qualificati.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold mb-6">
            Vieni a conoscerci
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            La porta della nostra palestra è sempre aperta. Prenota una lezione di prova 
            gratuita e scopri l'atmosfera dell'Academy.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Academy;
